import config from "@app/lib/api/config";
import { isArtifactPreviewHostMessage } from "@app/lib/api/files/html_artifact_preview";
import { getFilePathViewUrl } from "@app/lib/swr/files";
import type { LightWorkspaceType } from "@app/types/user";
import { cn } from "@dust-tt/sparkle";
import { useCallback, useEffect, useRef, useState } from "react";

// A file backed by a resource is served by id; one that only exists in a file system by its path.
export type HtmlArtifactPreviewSource =
  | { fileId: string }
  | { filePath: string };

function htmlArtifactPreviewUrl(
  owner: LightWorkspaceType,
  source: HtmlArtifactPreviewSource,
  theme: "light" | "dark",
  lastModifiedMs?: number
): string {
  const version = lastModifiedMs ? `&v=${lastModifiedMs}` : "";
  return "fileId" in source
    ? `${config.getApiBaseUrl()}/api/w/${owner.sId}/files/${source.fileId}/artifact-preview?theme=${theme}${version}`
    : `${getFilePathViewUrl(owner, source.filePath)}?preview=html&theme=${theme}${version}`;
}

function themeFromDocument(): "light" | "dark" {
  if (typeof document === "undefined") {
    return "light";
  }
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export function HtmlArtifactPreview({
  source,
  fileName,
  owner,
  onError,
  className,
  autoHeight = false,
  lastModifiedMs,
}: {
  source: HtmlArtifactPreviewSource;
  fileName: string;
  owner: LightWorkspaceType;
  onError?: () => void;
  className?: string;
  autoHeight?: boolean;
  lastModifiedMs?: number;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | undefined>(
    autoHeight ? 480 : undefined
  );
  const theme = themeFromDocument();

  const onMessage = useCallback(
    (event: MessageEvent) => {
      if (!autoHeight) {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      if (event.origin !== "null") {
        return;
      }
      if (!isArtifactPreviewHostMessage(event.data)) {
        return;
      }
      setHeight(Math.max(240, Math.min(Math.round(event.data.height), 8000)));
    },
    [autoHeight]
  );

  useEffect(() => {
    if (!autoHeight) {
      return;
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [autoHeight, onMessage]);

  return (
    <iframe
      ref={iframeRef}
      className={cn("h-full w-full border-0", className)}
      referrerPolicy="no-referrer"
      sandbox="allow-scripts"
      src={htmlArtifactPreviewUrl(owner, source, theme, lastModifiedMs)}
      style={autoHeight && height ? { height } : undefined}
      title={fileName}
      onError={onError}
    />
  );
}
